import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Accordion, AccordionDetails, AccordionSummary, Alert, Box, Button, Chip, CircularProgress, FormControl, InputLabel,
  ListItemText, ListSubheader, Menu, MenuItem, Paper, Select, TextField, Tooltip, Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LockIcon from '@mui/icons-material/Lock';
import HistoryIcon from '@mui/icons-material/History';
import { useSelector } from 'react-redux';
import { selectUser } from '@store/auth/authSlice';
import { systemPromptsService, SystemPromptGroup, SystemPromptVersion, SystemPromptView } from '@services/system-prompts.service';
import { toastService } from '../../services/toast.service';
import { getErrorMessage } from '@/utils/api-response.util';

const GROUP_LABELS: Record<SystemPromptGroup, string> = {
  recognition: 'Розпізнавання',
  calculation: 'Розрахунок',
  analysis: 'Аналіз',
};
const GROUP_ORDER: SystemPromptGroup[] = ['recognition', 'calculation', 'analysis'];

const formatDate = (iso: string | null | undefined) => (iso ? new Date(iso).toLocaleString('uk-UA') : '');

/**
 * Системні промпти (2026-09-30): реєстр і тексти - з бекенду (`GET /admin/system-prompts`), нічого не хардкодиться тут.
 * Адмін бачить захищену частину (шаблон з коду) і редагує лише відкритий блок; дозволені плейсхолдери - чіпи (клік вставляє).
 * Змінюють розробник і суперадмін, адмін - лише перегляд (бекенд теж перевіряє).
 */
export const AdminPromptsTab: React.FC = () => {
  const currentUser = useSelector(selectUser);
  const canEdit = (currentUser?.roles || []).map((r) => r.toLowerCase()).some((r) => r === 'pm' || r === 'superadmin');

  const [prompts, setPrompts] = useState<SystemPromptView[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedKey, setSelectedKey] = useState<string>('');
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [history, setHistory] = useState<SystemPromptVersion[] | null>(null);
  const [historyAnchor, setHistoryAnchor] = useState<HTMLElement | null>(null);
  const editorRef = useRef<HTMLTextAreaElement | null>(null);

  const selected = prompts.find((p) => p.key === selectedKey) ?? null;
  const editable = !!selected?.editable && canEdit;
  const dirty = !!selected && draft !== selected.text;

  // Відповідь для попереднього промпту (швидке перемикання) не показується під новим.
  const historyKeyRef = useRef<string | null>(null);
  const loadHistory = useCallback((key: string) => {
    historyKeyRef.current = key;
    setHistory(null);
    systemPromptsService.history(key)
      .then((items) => { if (historyKeyRef.current === key) setHistory(items); })
      .catch((error) => {
        if (historyKeyRef.current !== key) return;
        setHistory([]);
        toastService.error(getErrorMessage(error, 'Не вдалося завантажити історію'));
      });
  }, []);

  const applyPrompt = useCallback((updated: SystemPromptView) => {
    setPrompts((list) => list.map((p) => (p.key === updated.key ? updated : p)));
    setDraft(updated.text);
    setHistory(null); // журнал змінився - при наступному відкритті меню завантажиться заново
  }, []);

  // Меню "Історія": журнал вантажиться лише при відкритті (потрібен рідко; у списку промптів його нема).
  const openHistory = (anchor: HTMLElement) => {
    setHistoryAnchor(anchor);
    if (selected) loadHistory(selected.key);
  };

  useEffect(() => {
    systemPromptsService.list()
      .then((list) => {
        setPrompts(list);
        const first = list.find((p) => p.editable) ?? list[0];
        if (first) { setSelectedKey(first.key); setDraft(first.text); }
      })
      .catch((error) => toastService.error(getErrorMessage(error, 'Не вдалося завантажити промпти')))
      .finally(() => setLoading(false));
  }, []);

  const select = (key: string) => {
    if (dirty && !window.confirm('Є незбережені зміни. Перейти до іншого промпту?')) return;
    const next = prompts.find((p) => p.key === key);
    setSelectedKey(key);
    setDraft(next?.text ?? '');
    historyKeyRef.current = null;
    setHistory(null);
  };

  const insertPlaceholder = (name: string) => {
    const token = `{{${name}}}`;
    const el = editorRef.current;
    const start = el?.selectionStart ?? draft.length;
    const end = el?.selectionEnd ?? draft.length;
    setDraft(draft.slice(0, start) + token + draft.slice(end));
    requestAnimationFrame(() => { el?.focus(); el?.setSelectionRange(start + token.length, start + token.length); });
  };

  const save = async () => {
    if (!selected) return;
    setSaving(true);
    try {
      applyPrompt(await systemPromptsService.save(selected.key, draft));
      toastService.success('Промпт збережено - діє для нових запитів до AI');
    } catch (error) {
      toastService.error(getErrorMessage(error, 'Не вдалося зберегти промпт'));
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!selected || !window.confirm('Скинути до стандартного тексту? Поточний лишиться в історії змін.')) return;
    setSaving(true);
    try {
      applyPrompt(await systemPromptsService.reset(selected.key));
      toastService.success('Повернуто стандартний текст');
    } catch (error) {
      toastService.error(getErrorMessage(error, 'Не вдалося скинути промпт'));
    } finally {
      setSaving(false);
    }
  };

  // Захищена частина з виділеним місцем вставки редагованого тексту.
  const templateParts = useMemo(() => {
    if (!selected) return [];
    return selected.insertMarker ? selected.systemTemplate.split(selected.insertMarker) : [selected.systemTemplate];
  }, [selected]);

  if (loading) return <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>;
  if (!selected) return <Alert severity="info">Промптів немає</Alert>;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <FormControl sx={{ maxWidth: 420 }}>
        <InputLabel id="system-prompt-select">Промпт</InputLabel>
        <Select labelId="system-prompt-select" label="Промпт" value={selectedKey} onChange={(e) => select(e.target.value)}>
          {GROUP_ORDER.flatMap((group) => {
            const items = prompts.filter((p) => p.group === group);
            if (!items.length) return [];
            return [
              <ListSubheader key={`g-${group}`}>{GROUP_LABELS[group]}</ListSubheader>,
              ...items.map((p) => (
                <MenuItem key={p.key} value={p.key}>
                  {p.title}
                  {!p.editable && <Typography component="span" variant="caption" color="text.secondary" sx={{ ml: 1 }}>лише системний</Typography>}
                  {p.isCustom && <Chip size="small" label="змінено" color="warning" sx={{ ml: 1 }} />}
                </MenuItem>
              )),
            ];
          })}
        </Select>
      </FormControl>

      <Paper sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Box sx={{ display: 'flex', gap: 2, alignItems: 'flex-start' }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="h6">{selected.title}</Typography>
          <Typography variant="body2" color="text.secondary">{selected.description}</Typography>
          <Typography variant="caption" color="text.secondary">
            {!selected.editable ? 'Лише системний - не редагується'
              : selected.isCustom ? `Змінено ${formatDate(selected.updatedAt)}${selected.updatedBy?.email ? ` · ${selected.updatedBy.email}` : ''}`
              : 'Стандартний текст'}
          </Typography>
        </Box>
        {selected.editable && (
          <Button size="small" startIcon={<HistoryIcon />} onClick={(e) => openHistory(e.currentTarget)}>Історія</Button>
        )}
        </Box>

        {/* Дати змін (новіші зверху); клік - версія в редакторі, зберегти - окремою дією. */}
        <Menu anchorEl={historyAnchor} open={!!historyAnchor} onClose={() => setHistoryAnchor(null)}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }} transformOrigin={{ vertical: 'top', horizontal: 'right' }}
          slotProps={{ paper: { sx: { maxHeight: 420, minWidth: 280 } } }}>
          {!history ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}><CircularProgress size={20} /></Box>
          ) : history.length === 0 ? (
            <MenuItem disabled>Змін ще не було - діє стандартний текст</MenuItem>
          ) : history.map((version, i) => (
            <MenuItem key={version.id} onClick={() => { setDraft(version.text ?? selected.defaultText); setHistoryAnchor(null); }}>
              <ListItemText
                primary={`${formatDate(version.updatedAt) || 'Без дати'}${i === 0 ? ' · поточна' : ''}`}
                secondary={`${version.updatedBy?.email ?? (version.updatedBy ? `id ${version.updatedBy.id}` : 'автор невідомий')}${version.text === null ? ' · скинуто до стандартного' : ''}`}
              />
            </MenuItem>
          ))}
        </Menu>

        {(selected.group === 'recognition' || selected.group === 'calculation') && selected.editable && (
          <Alert severity="info">
            Зміна діє для нових розпізнавань і нових продуктів. Уже закешовані результати (раніше розібрані тексти й продукти) не змінюються.
          </Alert>
        )}
        {selected.editable && !canEdit && (
          <Alert severity="warning">Змінювати промпти можуть лише розробник і суперадмін.</Alert>
        )}

        <Accordion disableGutters variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <LockIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
            <Typography variant="body2">Системна частина (захищена, для розуміння)</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <Box component="pre" sx={{ m: 0, whiteSpace: 'pre-wrap', fontFamily: 'monospace', fontSize: 12, color: 'text.secondary' }}>
              {templateParts.map((part, i) => (
                <React.Fragment key={i}>
                  {part}
                  {i < templateParts.length - 1 && (
                    <Box component="span" sx={{ bgcolor: 'warning.light', color: 'warning.contrastText', px: 0.5, borderRadius: 0.5 }}>
                      {'⟨ редагований текст нижче ⟩'}
                    </Box>
                  )}
                </React.Fragment>
              ))}
            </Box>
          </AccordionDetails>
        </Accordion>

        {selected.editable && (
          <>
            {selected.placeholders.length > 0 && (
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, alignItems: 'center' }}>
                <Typography variant="body2" color="text.secondary">Дозволені плейсхолдери:</Typography>
                {selected.placeholders.map((ph) => (
                  <Tooltip key={ph.name} title={ph.description}>
                    <Chip size="small" variant="outlined" label={`{{${ph.name}}}`} onClick={editable ? () => insertPlaceholder(ph.name) : undefined} />
                  </Tooltip>
                ))}
              </Box>
            )}

            <TextField
              multiline
              minRows={12}
              fullWidth
              value={draft}
              onChange={(e) => setDraft(e.target.value)}
              inputRef={editorRef}
              disabled={!editable || saving}
              helperText="Інші плейсхолдери заборонені - збереження їх відхилить."
              InputProps={{ sx: { fontFamily: 'monospace', fontSize: 13 } }}
            />

            {editable && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Button variant="contained" onClick={save} disabled={!dirty || saving || !draft.trim()}>
                  {saving ? <CircularProgress size={20} color="inherit" /> : 'Зберегти'}
                </Button>
                <Button onClick={() => setDraft(selected.text)} disabled={!dirty || saving}>Скасувати зміни</Button>
                <Button onClick={() => setDraft(selected.defaultText)} disabled={saving || draft === selected.defaultText}>
                  Показати стандартний
                </Button>
                <Button color="warning" onClick={reset} disabled={!selected.isCustom || saving}>Скинути до стандартного</Button>
              </Box>
            )}

          </>
        )}
      </Paper>

    </Box>
  );
};
